import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "react-bootstrap";
import { toast } from "react-toastify";
import PageHeader1 from "../../components/common/PageHeader1";
import { submitPlanApi, getPlanByIdApi } from "../../services/Plan";

function CreatePlan() {
  const navigate = useNavigate();
  const { id } = useParams();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm();

  const [descriptions, setDescriptions] = useState([""]);

  const isIntroductory = watch("isIntroductory") === "true";

  /* =======================
     Fetch Plan (Edit Case)
     ======================= */
  useEffect(() => {
    if (id) {
      (async () => {
        const res = await getPlanByIdApi(id);
        if (res?.data) {
          const r = res.data;

          setValue("name", r.name);
          setValue("type", r.type);
          setValue("price", r.price);
          setValue("isIntroductory", r.isIntroductory ? "true" : "false");
          setValue("originalPrice", r.originalPrice || "");
          setValue("introDuration", r.durationInMonths || "");

          setDescriptions(
            Array.isArray(r.description) && r.description.length
              ? r.description
              : [""]
          );
        }
      })();
    }
  }, [id, setValue]);

  /* =======================
     Description Handlers
     ======================= */
  const handleDescriptionChange = (index, value) => {
    const updated = [...descriptions];
    updated[index] = value;
    setDescriptions(updated);
  };

  const addDescription = () => {
    setDescriptions([...descriptions, ""]);
  };

  const removeDescription = (index) => {
    const updated = descriptions.filter((_, i) => i !== index);
    setDescriptions(updated);
  };

  /* =======================
     Submit Handler
     ======================= */
  const handleFinalSubmit = async (formData) => {
    const payload = {
      action: id ? "edit" : "add",
      data: {
        name: formData.name,
        type: formData.type,
        price: Number(formData.price),
        originalPrice: formData.originalPrice
          ? Number(formData.originalPrice)
          : null,
        isIntroductory: formData.isIntroductory === "true",
        durationInMonths: formData.introDuration
          ? Number(formData.introDuration)
          : null,
        description: descriptions.filter((d) => d.trim() !== ""),
      },
    };
    console.log("SUBMIT PAYLOAD 👉", payload); // 👈 DEBUG

    if (id) {
      payload.data._id = id;
    }

    const res = await submitPlanApi(payload);

    if (res) {
      toast.success(id ? "Plan updated successfully" : "Plan added successfully");
      if (!id) {
        reset();
        setDescriptions([""]);
      }
      navigate("/plan-list");
    }
  };

  return (
    <div className="container">
      <PageHeader1
        pagetitle={id ? "Edit Plan" : "Add Plan"}
        navigation="Plans"
        navlink="/plan-list"
        current={id ? "Edit" : "Add"}
      />

      <div className="col-lg-10 mx-auto">
        <div className="card p-4 shadow-sm">
          <form onSubmit={handleSubmit(handleFinalSubmit)} className="row g-3">

            <div className="col-md-6">
              <label className="form-label">Name</label>
              <input
                className="form-control"
                {...register("name", { required: "Name is required" })}
              />
              {errors.name && <p className="text-danger">{errors.name.message}</p>}
            </div>

            <div className="col-md-6">
              <label className="form-label">Type</label>
              <select
                className="form-control"
                {...register("type", { required: "Type is required" })}
              >
                <option value="">Select Type</option>
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
              </select>
              {errors.type && <p className="text-danger">{errors.type.message}</p>}
            </div>

            <div className="col-md-6">
              <label className="form-label">Price</label>
              <input
                type="number"
                step="0.01"
                className="form-control"
                {...register("price", { required: "Price is required" })}
              />
              {errors.price && <p className="text-danger">{errors.price.message}</p>}
            </div>

            <div className="col-md-6">
              <label className="form-label">Is Introductory Price</label>
              <select
                className="form-control"
                {...register("isIntroductory")}
              >
                <option value="false">No</option>
                <option value="true">Yes</option>
              </select>
            </div>

            {isIntroductory && (
              <>
                <div className="col-md-6">
                  <label className="form-label">Original Price</label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-control"
                    {...register("originalPrice", {
                      required: "Original price is required",
                    })}
                  />
                  {errors.originalPrice && (
                    <p className="text-danger">{errors.originalPrice.message}</p>
                  )}
                </div>

                <div className="col-md-6">
                  <label className="form-label">Introductory Duration (Months)</label>
                  <input
                    type="number"
                    className="form-control"
                    {...register("introDuration", {
                      required: "Duration is required",
                    })}
                  />
                  {errors.introDuration && (
                    <p className="text-danger">{errors.introDuration.message}</p>
                  )}
                </div>
              </>
            )}

            {/* Descriptions */}
            <div className="col-12">
              <label className="form-label">Descriptions</label>
              {descriptions.map((desc, index) => (
                <div key={index} className="d-flex mb-2">
                  <input
                    type="text"
                    className="form-control me-2"
                    value={desc}
                    onChange={(e) =>
                      handleDescriptionChange(index, e.target.value)
                    }
                    placeholder={`Description ${index + 1}`}
                  />
                  {descriptions.length > 1 && (
                    <Button
                      variant="danger"
                      onClick={() => removeDescription(index)}
                    >
                      Remove
                    </Button>
                  )}
                </div>
              ))}
              <Button variant="success" onClick={addDescription}>
                + Add More
              </Button>
            </div>

            <div className="col-12 text-center mt-4">
              <Button type="submit" className="btn btn-primary px-5 me-3">
                {id ? "Update" : "Save"}
              </Button>
              <Button
                variant="secondary"
                className="px-5"
                onClick={() => navigate("/plan-list")}
              >
                Back
              </Button>
            </div>

          </form>
        </div>
      </div>
    </div>
  );
}

export default CreatePlan;
